import React, { useEffect, useState } from 'react';
import { Plus, Calendar, User as UserIcon } from 'lucide-react';
import { Task } from '../../types';
import { tasksService } from '../../services/tasksService';
import { echoBrainService, type EchoBrainProject } from '../../services/echoBrainService';
import { usersService } from '../../services/usersService';

interface QuickAddTaskProps {
  orgId: string;
  currentUserId: string;
  defaultEngagementId?: string;
  defaultMilestoneId?: string;
  defaultOpportunityId?: string;
  defaultLeadId?: string;
  defaultParentTaskId?: string;
  allUsers?: Array<{ id: string; name?: string; email?: string }>;
  compact?: boolean; // true = only title + Enter, false = with assignee/date
  onTaskCreated?: (taskId: string) => void;
}

const QuickAddTask: React.FC<QuickAddTaskProps> = ({
  orgId,
  currentUserId,
  defaultEngagementId,
  defaultMilestoneId,
  defaultOpportunityId,
  defaultLeadId,
  defaultParentTaskId,
  allUsers = [],
  compact = false,
  onTaskCreated,
}) => {
  const [title, setTitle] = useState('');
  const [assignedTo, setAssignedTo] = useState(currentUserId);
  const [loadedUsers, setLoadedUsers] = useState<Array<{ id: string; name?: string; email?: string }>>([]);
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [category, setCategory] = useState<string>('');
  const [echoBrainProjects, setEchoBrainProjects] = useState<EchoBrainProject[]>([]);
  const [echoBrainProjectId, setEchoBrainProjectId] = useState('');
  const [echoBrainProjectsError, setEchoBrainProjectsError] = useState<string | null>(null);
  const [showExpanded, setShowExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const userOptions = allUsers.length > 0
    ? allUsers
    : loadedUsers.length > 0
      ? loadedUsers
      : currentUserId
        ? [{ id: currentUserId, name: 'Current user' }]
        : [];

  useEffect(() => {
    if (allUsers.length > 0 || !orgId) return;

    usersService
      .getUsers(orgId)
      .then((users) => setLoadedUsers(users.map((user) => ({
        id: user.id,
        name: user.name,
        email: user.email,
      }))))
      .catch((error) => console.warn('[QuickAddTask] Failed to load users:', error));
  }, [allUsers.length, orgId]);

  useEffect(() => {
    setAssignedTo(currentUserId);
  }, [currentUserId]);

  useEffect(() => {
    if (category !== 'technical' || echoBrainProjects.length > 0) return;

    setEchoBrainProjectsError(null);
    echoBrainService
      .getProjects()
      .then(setEchoBrainProjects)
      .catch((error: Error) => {
        console.warn('[QuickAddTask] Failed to load Echo-Brain projects:', error.message);
        setEchoBrainProjectsError(error.message || 'Failed to load Echo-Brain projects');
      });
  }, [category, echoBrainProjects.length]);

  const handleSubmit = async () => {
    if (!title.trim() || isSubmitting) return;
    if (category === 'technical' && !echoBrainProjectId) {
      alert('Please select an Echo-Brain project before creating a technical task.');
      return;
    }
    setIsSubmitting(true);

    try {
      const taskData: Omit<Task, 'id'> = {
        title: title.trim(),
        description: '',
        assignedTo: assignedTo || currentUserId,
        createdBy: currentUserId,
        visibility: 'public',
        orgId,
        dueDate: new Date(dueDate),
        completed: false,
        priority,
        type: category === 'technical' ? 'technical' : 'follow_up',
        leadId: defaultLeadId || '',
        engagementId: defaultEngagementId,
        milestoneId: defaultMilestoneId,
        opportunityId: defaultOpportunityId,
        parentTaskId: defaultParentTaskId,
      };

      const taskId = await tasksService.createTask(taskData);
      
      // If this is a sub-task, link to parent
      if (defaultParentTaskId) {
        await tasksService.addSubTask(defaultParentTaskId, taskData);
      }

      onTaskCreated?.(taskId);

      // Sync to Echo-Brain if category is technical
      if (category === 'technical') {
        const assignedUser = userOptions.find(u => u.id === (assignedTo || currentUserId));
        echoBrainService.syncTask({
          id: taskId,
          title: title.trim(),
          description: `Echo-Brain project: ${echoBrainProjects.find(project => project.id === echoBrainProjectId)?.name || echoBrainProjectId}`,
          assigned_to: assignedTo || currentUserId,
          assigned_to_name: assignedUser?.name || assignedUser?.email || undefined,
          deadline: dueDate || undefined,
          priority,
          category: 'technical',
          source: 'AIDELINE',
          project_id: echoBrainProjectId,
        }).catch((err: Error) => console.warn('[EchoBrain] Task sync failed:', err.message));
      }
      setTitle('');
      setCategory('');
      setEchoBrainProjectId('');
      setShowExpanded(false);
    } catch (error) {
      console.error('Error creating task:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Plus className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-txt-muted" />
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onFocus={() => setShowExpanded(true)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
              placeholder="Aufgabe hinzufuegen..."
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-transparent border border-dashed border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent text-txt-muted"
            />
          </div>
          {title.trim() && (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-2.5 py-1.5 bg-accent text-txt-inverted rounded-lg text-xs font-medium hover:bg-accent-bright disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? '...' : 'OK'}
            </button>
          )}
        </div>
        {showExpanded && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <UserIcon className="w-3 h-3 text-txt-muted" />
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="text-xs border-0 bg-bg-surface rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-accent/30"
              >
                {userOptions.map(u => (
                  <option key={u.id} value={u.id}>{u.name || u.email || u.id}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => { setShowExpanded(false); setTitle(''); }}
              className="text-xs text-txt-muted hover:text-txt-secondary ml-auto"
            >
              Abbrechen
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border border-default rounded-xl p-3 bg-bg-card">
      <div className="flex items-center gap-2">
        <Plus className="w-4 h-4 text-txt-muted flex-shrink-0" />
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !showExpanded) handleSubmit(); }}
          onFocus={() => setShowExpanded(true)}
          placeholder="Neue Aufgabe..."
          className="flex-1 text-sm bg-transparent focus:outline-none text-txt-muted"
        />
        {title.trim() && (
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-3 py-1 bg-accent text-txt-inverted rounded-lg text-xs font-medium hover:bg-accent-bright disabled:opacity-50 transition-colors"
          >
            {isSubmitting ? '...' : 'Erstellen'}
          </button>
        )}
      </div>

      {showExpanded && (
        <div className="mt-2 pt-2 border-t border-subtle flex items-center gap-2 flex-wrap">
          {/* Assignee */}
          <div className="flex items-center gap-1">
            <UserIcon className="w-3 h-3 text-txt-muted" />
            <select
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              className="text-xs border-0 bg-bg-surface rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-accent/30"
            >
              {userOptions.map(u => (
                <option key={u.id} value={u.id}>{u.name || u.email || u.id}</option>
              ))}
            </select>
          </div>

          {/* Due Date */}
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3 text-txt-muted" />
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="text-xs border-0 bg-bg-surface rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-accent/30"
            />
          </div>

          {/* Priority */}
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as 'low' | 'medium' | 'high')}
            className="text-xs border-0 bg-bg-surface rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-accent/30"
          >
            <option value="low">Niedrig</option>
            <option value="medium">Mittel</option>
            <option value="high">Hoch</option>
          </select>
          {/* Category — if "technical", task syncs to Echo-Brain */}
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className={`text-xs border-0 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-accent/30 ${
              category === 'technical' ? 'bg-dev-soft text-dev' : 'bg-bg-surface'
            }`}
            title="Kategorie (technical = Echo-Brain Sync)"
          >
            <option value="">Kategorie...</option>
            <option value="technical">🔧 Technical → Echo-Brain</option>
            <option value="sales">Sales</option>
            <option value="marketing">Marketing</option>
            <option value="support">Support</option>
            <option value="general">General</option>
          </select>

          {category === 'technical' && (
            <select
              value={echoBrainProjectId}
              onChange={(e) => setEchoBrainProjectId(e.target.value)}
              className={`text-xs border-0 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-accent/30 ${
                !echoBrainProjectId ? 'bg-danger-soft text-danger' : 'bg-dev-soft text-dev'
              }`}
              title="Echo-Brain project"
            >
              <option value="">Echo-Brain project...</option>
              {echoBrainProjects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          )}

          {category === 'technical' && echoBrainProjectsError && (
            <span className="text-xs text-danger">{echoBrainProjectsError}</span>
          )}

          <button
            onClick={() => { setShowExpanded(false); setTitle(''); }}
            className="text-xs text-txt-muted hover:text-txt-secondary ml-auto"
          >
            Abbrechen
          </button>
        </div>
      )}
    </div>
  );
};

export default QuickAddTask;
